/**
 * PerformanceTracking — Iter 103.
 * Per-staff daily/weekly/monthly activity + working-time roll-up.
 * PDF/CSV download via the shared /workforce/report/activity endpoint.
 */
import React, { useEffect, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { FilePdf, FileCsv, ChartLineUp, MagnifyingGlass } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";
function getToken() { return localStorage.getItem("rescigo_token") || ""; }
function todayIso() { return new Date().toISOString().slice(0, 10); }
function daysAgoIso(n) { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); }

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = d.toLocaleString("en-US", { month: "short" });
    const hh = String(d.getHours()).padStart(2, "0");
    const mn = String(d.getMinutes()).padStart(2, "0");
    return `${dd} ${mm} · ${hh}:${mn}`;
  } catch { return "—"; }
}

const ROLE_STYLE = {
  field_staff:  "bg-[#DDE7F1] text-[#1F3A57] border-[#1F3A57]",
  office_staff: "bg-[#F0F7EE] text-[#1A3317] border-[#B5D5AC]",
  office:       "bg-[#F0F7EE] text-[#1A3317] border-[#B5D5AC]",
  manager:      "bg-[#F0E5F0] text-[#5A2E5C] border-[#C7A6C7]",
  admin:        "bg-[#F9E4B7] text-[#7A5A11] border-[#F0DDB2]",
  service_coordinator: "bg-[#EDF7EA] text-[#1A4E12] border-[#B5D5AC]",
};

export default function PerformanceTracking() {
  const [preset, setPreset] = useState("week");
  const [start, setStart] = useState(daysAgoIso(7));
  const [end, setEnd]     = useState(todayIso());
  const [data, setData]   = useState(null);
  const [q, setQ]         = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [busy, setBusy]   = useState(false);

  useEffect(() => {
    if (preset === "today")   { setStart(todayIso());       setEnd(todayIso()); }
    else if (preset === "week")    { setStart(daysAgoIso(7));  setEnd(todayIso()); }
    else if (preset === "month")   { setStart(daysAgoIso(30)); setEnd(todayIso()); }
    else if (preset === "quarter") { setStart(daysAgoIso(90)); setEnd(todayIso()); }
  }, [preset]);

  const buildParams = () => {
    const p = new URLSearchParams();
    p.set("start_date", start + "T00:00:00");
    p.set("end_date",   end + "T23:59:59");
    return p;
  };

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/workforce/performance?${buildParams()}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [preset, start, end]);

  const download = async (format) => {
    setBusy(true);
    try {
      const params = buildParams(); params.set("format", format);
      const r = await fetch(`${BASE}/workforce/report/activity?${params}`,
        { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `performance_${start}_${end}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded`);
    } catch { toast.error("Download failed"); }
    finally { setBusy(false); }
  };

  const rows = (data?.rows || []).filter(r => {
    if (roleFilter && r.role !== roleFilter) return false;
    if (!q.trim()) return true;
    const k = q.trim().toLowerCase();
    return (r.name || "").toLowerCase().includes(k) ||
           (r.email || "").toLowerCase().includes(k) ||
           (r.role  || "").toLowerCase().includes(k);
  });

  const labels = data?.bucket_labels || [];
  const roles = Array.from(new Set((data?.rows || []).map(r => r.role).filter(Boolean)));

  // Aggregate totals
  const grand = rows.reduce((acc, r) => {
    acc.working_hours += r.active_hours || 0;
    acc.break_mins    += r.break_minutes || 0;
    acc.days          += r.days_worked || 0;
    acc.activities    += r.total || 0;
    return acc;
  }, { working_hours: 0, break_mins: 0, days: 0, activities: 0 });

  return (
    <AppShell title="Performance Tracking"
      subtitle="Module 12 · Workforce · Analytics">
      {/* Controls */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-4 mb-4"
           data-testid="perf-controls">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex items-center gap-1">
            {[
              { k: "today",   label: "Today" },
              { k: "week",    label: "7 days" },
              { k: "month",   label: "30 days" },
              { k: "quarter", label: "90 days" },
              { k: "custom",  label: "Custom" },
            ].map(p => (
              <button key={p.k} type="button" onClick={() => setPreset(p.k)}
                      data-testid={`perf-preset-${p.k}`}
                      className={`h-9 px-3 rounded-md text-xs border transition-colors ${
                        preset === p.k ? "bg-[#2D5A27] text-white border-[#2D5A27]"
                                       : "bg-white text-[#1A1A1A] border-[#E5E2D9] hover:border-[#2D5A27]"
                      }`}>{p.label}</button>
            ))}
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-[0.2em] text-[#5C5A55]">From</label>
            <input type="date" value={start} data-testid="perf-from"
                    onChange={e => { setStart(e.target.value); setPreset("custom"); }}
                    className="h-9 px-2 border border-[#E5E2D9] rounded-md text-sm block"/>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-[0.2em] text-[#5C5A55]">To</label>
            <input type="date" value={end} data-testid="perf-to"
                    onChange={e => { setEnd(e.target.value); setPreset("custom"); }}
                    className="h-9 px-2 border border-[#E5E2D9] rounded-md text-sm block"/>
          </div>
          <select value={roleFilter} onChange={e => setRoleFilter(e.target.value)}
                  data-testid="perf-role-filter"
                  className="h-9 px-2 border border-[#E5E2D9] rounded-md text-sm bg-white">
            <option value="">All roles</option>
            {roles.map(r => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select>
          <div className="relative flex-1 min-w-[220px]">
            <MagnifyingGlass size={14} className="absolute left-2 top-2.5 text-[#8A8780]"/>
            <input value={q} onChange={e => setQ(e.target.value)}
                    placeholder="Search staff / email"
                    data-testid="perf-search"
                    className="w-full h-9 pl-7 pr-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </div>
          <button onClick={() => download("pdf")} disabled={busy}
                  data-testid="perf-pdf"
                  className="h-9 px-3 bg-[#7A2E18] hover:bg-[#5A1F10] text-white rounded-md text-xs font-semibold inline-flex items-center gap-1.5">
            <FilePdf size={13} weight="fill"/> PDF
          </button>
          <button onClick={() => download("csv")} disabled={busy}
                  data-testid="perf-csv"
                  className="h-9 px-3 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-xs font-semibold inline-flex items-center gap-1.5">
            <FileCsv size={13} weight="fill"/> CSV
          </button>
        </div>

        {data && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="perf-grand">
            <SumStat label="Staff tracked" value={rows.length}/>
            <SumStat label="Working hours" value={grand.working_hours.toFixed(1)}/>
            <SumStat label="Break minutes" value={grand.break_mins}/>
            <SumStat label="Activities logged" value={grand.activities}/>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-hidden"
           data-testid="perf-table-card">
        <div className="px-5 py-3 border-b border-[#E5E2D9] text-[10px] uppercase tracking-[0.2em] text-[#5C5A55] flex justify-between">
          <span><ChartLineUp size={13} className="inline mr-1"/> Per-staff performance · {data?.label || "…"}</span>
          <span>{rows.length} staff shown</span>
        </div>
        <div className="overflow-x-auto max-h-[70vh]">
          <table className="w-full text-xs" data-testid="perf-table">
            <thead className="sticky top-0 bg-[#F7F6F2] z-10">
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#5C5A55]">
                <th className="px-3 py-2">Staff</th>
                <th className="px-2 py-2 text-right">Days</th>
                <th className="px-2 py-2 text-right whitespace-nowrap">First login</th>
                <th className="px-2 py-2 text-right whitespace-nowrap">Last logout</th>
                <th className="px-2 py-2 text-right">Hours</th>
                <th className="px-2 py-2 text-right">Break m</th>
                {labels.map(l => (
                  <th key={l} className="px-2 py-2 text-right whitespace-nowrap">{l}</th>
                ))}
                <th className="px-2 py-2 text-right bg-[#EDF7EA]">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7 + labels.length} className="px-5 py-8 text-center text-[#8A8780]">No activity in this window.</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.user_id} data-testid={`perf-row-${r.user_id}`}
                    className={i % 2 ? "bg-white" : "bg-[#FAFAF7]"}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-[#1A1A1A]">{r.name || r.user_id?.slice(0,8)}</div>
                    <span className={`inline-block text-[9px] uppercase tracking-[0.12em] font-semibold px-1.5 rounded border ${ROLE_STYLE[r.role] || "bg-[#F0EFEA] text-[#5C5A55] border-[#E5E2D9]"}`}>
                      {(r.role || "—").replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-right font-mono">{r.days_worked}</td>
                  <td className="px-2 py-2 text-right font-mono text-[11px] whitespace-nowrap">{fmtTs(r.first_login)}</td>
                  <td className="px-2 py-2 text-right font-mono text-[11px] whitespace-nowrap">{fmtTs(r.last_logout)}</td>
                  <td className="px-2 py-2 text-right font-mono">{r.active_hours.toFixed(1)}</td>
                  <td className="px-2 py-2 text-right font-mono">{r.break_minutes}</td>
                  {labels.map(l => (
                    <td key={l} className="px-2 py-2 text-right font-mono">
                      {(r.buckets && r.buckets[l]) ? r.buckets[l] : ""}
                    </td>
                  ))}
                  <td className="px-2 py-2 text-right font-mono font-semibold bg-[#F0F7EE]">{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

function SumStat({ label, value }) {
  return (
    <div className="rounded-md border border-[#E5E2D9] bg-[#FAFAF7] px-3 py-2">
      <div className="text-[9px] uppercase tracking-[0.16em] text-[#8A8780]">{label}</div>
      <div className="text-lg font-heading text-[#1A1A1A]">{value}</div>
    </div>
  );
}
